import { classes } from 'html-classes';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import { ContentSkeleton } from './ContentSkeleton';
import type { TabsProps } from './interfaces';
import styles from './Tabs.module.scss';

const Tabs = ({
  tabs,
  className,
  tabsClassName,
  tabClassName,
  defaultValue,
  activeClassName,
  contentWrapperClassName,
  isLoading,
  onChange,
}: TabsProps) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<string>(defaultValue ?? tabs[0]?.id ?? '');
  const [tabIndicatorRect, setTabIndicatorRect] = useState({
    left: 0,
    width: 0,
  });

  const tabsRef = useRef<Nullable<HTMLDivElement>>(null);
  const tabRefMap = useRef(new Map<string, HTMLDivElement>());

  const calculateTabIndicatorPosition = (node: HTMLDivElement) => {
    const { width, left } = node.getBoundingClientRect();
    const container = tabsRef.current;
    const containerRect = container?.getBoundingClientRect();
    const scrollOffset = container?.scrollLeft ?? 0;
    const leftNormalized = containerRect
      ? left - containerRect.left + scrollOffset
      : 0;
    setTabIndicatorRect({ left: leftNormalized, width });
  };

  const handleLoadTab = (id: string) => (node: Nullable<HTMLDivElement>) => {
    if (!node) {
      tabRefMap.current.delete(id);
      return;
    }
    tabRefMap.current.set(id, node);
    if (id === tab && !tabIndicatorRect.left && !tabIndicatorRect.width) {
      calculateTabIndicatorPosition(node);
    }
  };

  const handleChangeTab = (value: string) => () => {
    setTab(value);
    onChange?.(value);
  };

  const getIndicatorPositionStyles = () =>
    ({
      '--tab-indicator-translate': `${isLoading ? 0 : tabIndicatorRect.left}px`,
      '--tab-indicator-width': `${isLoading ? 0 : tabIndicatorRect.width}px`,
    }) as CSSProperties;

  useEffect(() => {
    const node = tabRefMap.current.get(tab);
    if (node) calculateTabIndicatorPosition(node);
  }, [tab, tabs]);

  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some(({ id }) => id === tab)) {
      setTab(tabs[0]?.id ?? '');
    }
  }, [tabs]);

  const activeContent = tabs.find(({ id }) => id === tab)?.content ?? null;

  return (
    <div
      className={classes([
        styles.root,
        !tabs.length && styles.empty,
        className,
      ])}
    >
      <div
        className={classes([styles.tabs, tabsClassName])}
        style={getIndicatorPositionStyles()}
        ref={tabsRef}
      >
        {isLoading && (
          <>
            <Skeleton containerClassName={styles.skeleton} />
            <Skeleton containerClassName={styles.skeleton} />
          </>
        )}
        {!isLoading &&
          tabs.map(({ id, label }) => (
            <div
              key={`tab_${id}`}
              role="tab"
              tabIndex={-1}
              ref={handleLoadTab(id)}
              className={classes([
                styles.tab,
                tabClassName,
                id === tab && (activeClassName || styles.active),
              ])}
              onClick={handleChangeTab(id)}
            >
              {label ?? t(id)}
            </div>
          ))}
      </div>

      {!isLoading && (
        <div
          className={classes([styles.contentWrapper, contentWrapperClassName])}
        >
          {activeContent}
        </div>
      )}
      {isLoading && <ContentSkeleton />}
    </div>
  );
};

export default Tabs;
